import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_GET_ID = (item) => item?.id;
const CACHE_TTL_MS = 45_000;
const PAGE_CACHE = new Map();
const PAGE_INFLIGHT = new Map();
const FN_IDS = new WeakMap();
let FN_ID_SEQ = 1;

function getFnId(listFn) {
  if (!FN_IDS.has(listFn)) {
    FN_IDS.set(listFn, FN_ID_SEQ);
    FN_ID_SEQ += 1;
  }
  return FN_IDS.get(listFn);
}

function makeBaseKey(fnId, query, ativo, pageSize) {
  const ativoKey = ativo === undefined ? "all" : String(ativo);
  return `fn:${fnId}|q:${query}|a:${ativoKey}|ps:${pageSize}`;
}

function makePageKey(baseKey, offset) {
  return `${baseKey}|off:${offset}`;
}

function readPageCache(pageKey) {
  const cached = PAGE_CACHE.get(pageKey);
  if (!cached) return null;
  if (Date.now() - cached.ts > CACHE_TTL_MS) {
    PAGE_CACHE.delete(pageKey);
    return null;
  }
  return cached.data;
}

function writePageCache(pageKey, data) {
  PAGE_CACHE.set(pageKey, { ts: Date.now(), data });
}

function clearCacheByFnId(fnId) {
  const prefix = `fn:${fnId}|`;
  for (const key of PAGE_CACHE.keys()) {
    if (key.startsWith(prefix)) PAGE_CACHE.delete(key);
  }
}

function mergeUniqueById(prev, next, getId) {
  const map = new Map();
  prev.forEach((item) => map.set(getId(item), item));
  next.forEach((item) => map.set(getId(item), item));
  return Array.from(map.values());
}

function statusToAtivo(statusFilter) {
  if (statusFilter === "ativos") return 1;
  if (statusFilter === "inativos") return 0;
  return undefined;
}

export function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

export function useInfiniteBaseList({
  listFn,
  queryText,
  statusFilter,
  pageSize = 100,
  getId = DEFAULT_GET_ID,
}) {
  const debouncedQuery = useDebouncedValue(String(queryText ?? "").trim(), 260);
  const fnId = useMemo(() => getFnId(listFn), [listFn]);
  const requestSeqRef = useRef(0);
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState("");

  const ativo = useMemo(() => statusToAtivo(statusFilter), [statusFilter]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const loadPage = useCallback(
    async ({ reset = false } = {}) => {
      if (reset) {
        setLoading(true);
        loadingRef.current = true;
      } else {
        if (loadingRef.current || loadingMoreRef.current || !hasMoreRef.current) return;
        setLoadingMore(true);
        loadingMoreRef.current = true;
      }

      const requestSeq = ++requestSeqRef.current;
      const offset = reset ? 0 : offsetRef.current;
      const baseKey = makeBaseKey(fnId, debouncedQuery, ativo, pageSize);
      const pageKey = makePageKey(baseKey, offset);

      try {
        let data;
        const cachedPage = readPageCache(pageKey);

        if (cachedPage && !reset) {
          data = cachedPage;
        } else {
          let inflight = PAGE_INFLIGHT.get(pageKey);
          if (!inflight) {
            inflight = listFn({
              q: debouncedQuery,
              ativo,
              limit: pageSize,
              offset,
            }).finally(() => {
              PAGE_INFLIGHT.delete(pageKey);
            });
            PAGE_INFLIGHT.set(pageKey, inflight);
          }
          data = await inflight;
          writePageCache(pageKey, Array.isArray(data) ? data : []);
        }

        if (requestSeq !== requestSeqRef.current) return;

        const safeData = Array.isArray(data) ? data : [];
        offsetRef.current = offset + safeData.length;
        const nextHasMore = safeData.length === pageSize;
        hasMoreRef.current = nextHasMore;
        setHasMore(nextHasMore);
        setError("");
        setRows((prev) => (reset ? safeData : mergeUniqueById(prev, safeData, getId)));
      } catch (err) {
        if (requestSeq !== requestSeqRef.current) return;
        if (reset) setRows([]);
        hasMoreRef.current = false;
        setHasMore(false);
        setError(err?.response?.data?.error || err?.message || "Erro ao carregar dados.");
      } finally {
        if (requestSeq === requestSeqRef.current) {
          loadingRef.current = false;
          loadingMoreRef.current = false;
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [ativo, debouncedQuery, fnId, getId, listFn, pageSize]
  );

  useEffect(() => {
    offsetRef.current = 0;
    hasMoreRef.current = true;
    setHasMore(true);
    loadPage({ reset: true });
  }, [loadPage]);

  const reload = useCallback(() => {
    clearCacheByFnId(fnId);
    offsetRef.current = 0;
    hasMoreRef.current = true;
    setHasMore(true);
    loadPage({ reset: true });
  }, [fnId, loadPage]);

  const loadMore = useCallback(() => {
    loadPage({ reset: false });
  }, [loadPage]);

  return {
    rows,
    loading,
    loadingMore,
    hasMore,
    error,
    reload,
    loadMore,
  };
}
